<!--
  Planeación de materia prima por ritmo de máquina (reporte r118).

  Contesta, para el día de hoy: cuánta materia prima hay, cuánta se usó la
  semana pasada, cuánto produjo cada máquina y cuánta materia prima hace
  falta por día y por semana para decidir cuánto pedir y cuándo.

  Este componente no calcula nada: el cálculo vive en
  PlaneacionMateriaPrimaService (backend) y aquí solo se pinta lo que
  devuelve /api/PlaneacionMateriaPrima/Tablero. Cada instancia muestra su
  propia planta.

  Tres bloques: tarjetas del resumen (filtran la tabla al oprimirlas), tabla
  por materia prima con renglón desplegable (requerido por día y máquinas que
  la consumen) y tabla de producción por máquina con el ritmo por día de la
  semana. Al final, los artículos que producen sin receta de materia prima.
-->
<template>
  <v-col cols="12" class="pa-0">
    <div id="planeacion-mp">
      <div class="pm-head">
        <div>
          <h1>Planeación de materia prima por ritmo de máquina</h1>
          <div class="sub">{{ subtitulo }}</div>
        </div>
        <div class="der">
          <label>Almacén
            <select v-model="idAlmacen" @change="cargar()">
              <option :value="null">Todos</option>
              <option v-for="a in almacenes" :key="a.id" :value="a.id">{{ a.nombre }}</option>
            </select>
          </label>
          <label>Historia
            <select v-model.number="semanas" @change="cargar()">
              <option :value="2">2 semanas</option>
              <option :value="4">4 semanas</option>
              <option :value="8">8 semanas</option>
            </select>
          </label>
          <span class="reloj">{{ reloj }}</span>
          <button class="claro" :disabled="cargando" @click="refrescar">
            {{ cargando ? "consultando…" : "Actualizar ahora" }}
          </button>
        </div>
      </div>

      <main>
        <div v-if="error" class="err">{{ error }}</div>

        <div v-if="!tablero && cargando" class="vacio">Cargando…</div>
        <div v-else-if="!tablero" class="vacio">Sin datos.</div>

        <template v-else>
          <!-- Avisos del back: lo que hay que leer antes de confiar en las cifras. -->
          <div v-if="avisos.length" class="avisos">
            <div v-for="(a, i) in avisos" :key="i" class="aviso" :class="claseAviso(val(a, 'tipo', 'Tipo'))">
              <span class="tag">{{ etiquetaAviso(val(a, "tipo", "Tipo")) }}</span>
              <span>{{ val(a, "texto", "Texto") }}</span>
            </div>
          </div>
          <div class="corte mini">
            <template v-if="val(T, 'leyendaExistencia', 'LeyendaExistencia')">
              {{ val(T, "leyendaExistencia", "LeyendaExistencia") }}
              <template v-if="val(T, 'hayCorteInventario', 'HayCorteInventario')">
                Corte del {{ fecha(val(T, "fechaCorte", "FechaCorte")) }}.</template>
            </template>
            <template v-else-if="val(T, 'hayCorteInventario', 'HayCorteInventario')">
              Existencia calculada sobre el corte de inventario del
              {{ fecha(val(T, "fechaCorte", "FechaCorte")) }} más los movimientos posteriores.
            </template>
            <template v-else>No hay un corte de inventario vigente: la existencia se suma desde el primer movimiento.</template>
            · Ritmo calculado con {{ val(T, "semanasHistoria", "SemanasHistoria") }} semanas de historia
            · Pronóstico de mañana al {{ fecha(val(T, "finSemanaSiguiente", "FinSemanaSiguiente")) }}.
          </div>

          <!-- Tarjetas del resumen: se oprimen para filtrar la tabla de materias primas. -->
          <div class="cards">
            <div v-for="c in tarjetas" :key="c.clave"
                 class="card clic" :class="[c.color, { sel: filtro === c.clave }]"
                 :title="c.ayuda" @click="filtrar(c.clave)">
              <div class="n">{{ c.n }}</div>
              <div class="t">{{ c.titulo }}</div>
            </div>
          </div>

          <!-- Tabla por materia prima -->
          <div class="bloque">
            <div class="bt">
              Materias primas
              <span class="mini">
                · {{ mpsFiltradas.length }} de {{ mps.length }}
                <template v-if="filtro"> · filtro: {{ tituloFiltro }} —
                  <a href="#" @click.prevent="filtrar(null)">quitar</a></template>
                · oprime un renglón para ver el requerido por día y qué máquinas la consumen
              </span>
            </div>
            <div class="tabla"><table>
              <thead><tr>
                <th class="izq">Materia prima</th>
                <th>Semáforo</th>
                <th>Unidad</th>
                <th>Existencia</th>
                <th title="Reservado por órdenes vigentes. Informativo: no se resta de la existencia.">Reservado</th>
                <th title="Piezas producidas la semana pasada por la materia prima por pieza">Sem. pasada<br>teórico</th>
                <th title="Salidas de almacén registradas contra una orden la semana pasada">Sem. pasada<br>registrado</th>
                <th title="Requerido promedio por día en los días pronosticados">Ritmo<br>diario</th>
                <th title="De mañana al domingo de esta semana">Resto de<br>semana</th>
                <th title="De lunes a domingo de la semana siguiente">Semana<br>siguiente</th>
                <th>Se agota el</th>
                <th title="Lo que falta para cubrir hasta el domingo de la semana siguiente">Faltante</th>
                <th title="Se agota menos el tiempo de entrega del proveedor">Pedir<br>antes de</th>
              </tr></thead>
              <tbody>
                <template v-for="m in mpsFiltradas">
                  <tr :key="'mp' + val(m, 'idArticulo', 'IdArticulo')"
                      class="clic" :class="claseSemaforo(val(m, 'semaforo', 'Semaforo'))"
                      @click="abrir(val(m, 'idArticulo', 'IdArticulo'))">
                    <td class="izq">
                      <div class="cod">{{ codigo(m) }}
                        <span class="fl">{{ abierta === val(m, "idArticulo", "IdArticulo") ? "▴" : "▾" }}</span></div>
                      <div class="mini nom" :title="nombre(m)">{{ nombre(m) }}</div>
                      <div v-if="val(m, 'nota', 'Nota')" class="mini nota-mp">{{ nota(val(m, "nota", "Nota")) }}</div>
                    </td>
                    <td><span class="sem" :class="claseSemaforo(val(m, 'semaforo', 'Semaforo'))">
                      {{ textoSemaforo(val(m, "semaforo", "Semaforo")) }}</span></td>
                    <td>{{ (val(m, "unidad", "Unidad") || "").trim() }}</td>
                    <td class="num">{{ f2(val(m, "existencia", "Existencia")) }}</td>
                    <td class="num">{{ f2(val(m, "reservado", "Reservado")) }}</td>
                    <td class="num">{{ f2(val(m, "consumoSemanaPasadaTeorico", "ConsumoSemanaPasadaTeorico")) }}</td>
                    <td class="num">{{ f2(val(m, "consumoSemanaPasadaRegistrado", "ConsumoSemanaPasadaRegistrado")) }}</td>
                    <td class="num">{{ f2(val(m, "ritmoDiario", "RitmoDiario")) }}</td>
                    <td class="num">{{ f2(val(m, "requeridoRestoSemana", "RequeridoRestoSemana")) }}</td>
                    <td class="num">{{ f2(val(m, "requeridoSemanaSiguiente", "RequeridoSemanaSiguiente")) }}</td>
                    <td>{{ fecha(val(m, "seAgotaEl", "SeAgotaEl")) }}</td>
                    <td class="num" :class="{ mal: num(val(m, 'faltante', 'Faltante')) > 0 }">
                      {{ f2(val(m, "faltante", "Faltante")) }}</td>
                    <td>
                      <template v-if="val(m, 'sinTiempoEntrega', 'SinTiempoEntrega') === true && val(m, 'seAgotaEl', 'SeAgotaEl')">
                        <span class="mini sin-te" title="No hay tiempo de entrega de proveedor capturado para esta materia prima: captúralo en el catálogo de proveedores para calcular la fecha límite de pedido.">Sin tiempo de entrega capturado</span>
                      </template>
                      <template v-else>
                        {{ fecha(val(m, "pedirAntesDe", "PedirAntesDe")) }}
                        <div v-if="val(m, 'tiempoEntregaDias', 'TiempoEntregaDias') != null" class="mini">
                          entrega {{ f1(val(m, "tiempoEntregaDias", "TiempoEntregaDias")) }} d</div>
                      </template>
                    </td>
                  </tr>
                  <tr v-if="abierta === val(m, 'idArticulo', 'IdArticulo')"
                      :key="'det' + val(m, 'idArticulo', 'IdArticulo')" class="det">
                    <td colspan="13" class="izq">
                      <div class="paneles">
                        <div class="panel">
                          <div class="ph">Requerido por día
                            <span class="mini">· en {{ (val(m, "unidad", "Unidad") || "").trim() }} · existencia {{ f2(val(m, "existencia", "Existencia")) }}</span></div>
                          <table v-if="arr(val(m, 'dias', 'Dias')).length" class="sub">
                            <tr><th class="izq">Día</th><th>Requerido</th><th>Acumulado</th><th>Restante</th></tr>
                            <tr v-for="d in arr(val(m, 'dias', 'Dias'))" :key="val(d, 'fecha', 'Fecha')"
                                :class="{ neg: num(val(d, 'restante', 'Restante')) < 0 }">
                              <td class="izq">{{ val(d, "diaSemana", "DiaSemana") }} {{ fecha(val(d, "fecha", "Fecha")) }}</td>
                              <td class="num">{{ f2(val(d, "requerido", "Requerido")) }}</td>
                              <td class="num">{{ f2(val(d, "acumulado", "Acumulado")) }}</td>
                              <td class="num" :class="{ mal: num(val(d, 'restante', 'Restante')) < 0 }">
                                {{ f2(val(d, "restante", "Restante")) }}</td>
                            </tr>
                          </table>
                          <div v-else class="mini">Sin días pronosticados.</div>
                        </div>
                        <div class="panel">
                          <div class="ph">Máquinas que la consumen
                            <span class="mini">· {{ arr(val(m, "consumidores", "Consumidores")).length }}</span></div>
                          <table v-if="arr(val(m, 'consumidores', 'Consumidores')).length" class="sub">
                            <tr><th class="izq">Máquina</th><th class="izq">Artículo</th><th class="izq">Orden</th>
                              <th title="Materia prima por pieza, ya con merma">Por pieza</th>
                              <th title="Piezas promedio por día en los días pronosticados">Piezas/día</th>
                              <th>Requerido/día</th></tr>
                            <tr v-for="(c, i) in arr(val(m, 'consumidores', 'Consumidores'))" :key="i">
                              <td class="izq"><strong>{{ maquina(c) }}</strong></td>
                              <td class="izq">{{ val(c, "articulo", "Articulo") || "Sin nombre" }}</td>
                              <td class="izq">{{ val(c, "orden", "Orden") || "—" }}</td>
                              <td class="num">{{ f4(val(c, "porPieza", "PorPieza")) }}</td>
                              <td class="num">{{ f1(val(c, "piezasDia", "PiezasDia")) }}</td>
                              <td class="num">{{ f2(val(c, "requeridoDia", "RequeridoDia")) }}</td>
                            </tr>
                          </table>
                          <div v-else class="mini">Ninguna corrida activa consume esta materia prima.</div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
                <tr v-if="!mpsFiltradas.length">
                  <td colspan="13" class="vacio">
                    {{ mps.length ? "Ninguna materia prima con este filtro." : "Sin materias primas con movimientos ni recetas." }}
                  </td>
                </tr>
              </tbody>
            </table></div>
          </div>

          <!-- Tabla de producción por máquina -->
          <div class="bloque">
            <div class="bt">Producción por máquina
              <span class="mini">· una corrida por máquina y orden · piezas buenas más piezas reportadas con incidencia, por día de la semana, con las que se pronostica</span></div>
            <div class="tabla"><table>
              <thead><tr>
                <th class="izq">Máquina</th><th class="izq">Artículo</th><th class="izq">Orden</th>
                <th title="La orden sigue abierta y produjo en los últimos días: entra al pronóstico">Activa</th>
                <th>Sem. pasada<br>buenas</th><th title="Piezas reportadas con incidencia (scrap)">Sem. pasada<br>incidencias</th>
                <th v-for="d in diasSemana" :key="d">{{ d }}</th>
                <th title="El artículo tiene materia prima capturada en su receta">Receta<br>de MP</th>
                <th class="izq">Nota</th>
              </tr></thead>
              <tbody>
                <tr v-for="(p, i) in produccion" :key="i" :class="{ apagada: !val(p, 'activa', 'Activa') }">
                  <td class="izq"><strong>{{ maquina(p) }}</strong></td>
                  <td class="izq">{{ val(p, "articulo", "Articulo") || "Sin nombre" }}</td>
                  <td class="izq">{{ val(p, "orden", "Orden") || "—" }}</td>
                  <td><span class="pill" :class="val(p, 'activa', 'Activa') ? 'on' : 'off'">
                    {{ val(p, "activa", "Activa") ? "Activa" : "Inactiva" }}</span></td>
                  <td class="num">{{ f0(val(p, "semanaPasadaBuenas", "SemanaPasadaBuenas")) }}</td>
                  <td class="num">{{ f0(val(p, "semanaPasadaScrap", "SemanaPasadaScrap")) }}</td>
                  <td v-for="d in diasSemana" :key="d" class="num" :class="{ cero: !num(ritmo(p, d)) }">
                    {{ f1(ritmo(p, d)) }}</td>
                  <td><span class="pill" :class="val(p, 'tieneRecetaMP', 'TieneRecetaMP') ? 'on' : 'sin'">
                    {{ val(p, "tieneRecetaMP", "TieneRecetaMP") ? "Sí" : "No" }}</span></td>
                  <td class="izq mini nota-td" :class="{ reemplazada: esReemplazada(p) }"
                      :title="nota(val(p, 'nota', 'Nota'))">{{ nota(val(p, "nota", "Nota")) }}</td>
                </tr>
                <tr v-if="!produccion.length"><td colspan="16" class="vacio">Sin producción en el periodo.</td></tr>
              </tbody>
            </table></div>
          </div>

          <!-- Artículos sin receta de materia prima -->
          <div class="bloque" ref="sinReceta" :class="{ marcado: filtro === 'sinReceta' }">
            <div class="bt">Artículos sin receta de materia prima
              <span class="mini">· {{ sinReceta.length }}</span></div>
            <div class="caja">
              <template v-if="sinReceta.length">
                <p>Estos artículos producen pero no tienen receta de materia prima capturada:
                  captúrala en Configuración → Recetas para que aparezcan en el pronóstico.</p>
                <div class="chips">
                  <span v-for="a in sinReceta" :key="val(a, 'idArticulo', 'IdArticulo')" class="chip"
                        :title="nombre(a)">
                    {{ codigo(a) }}<span class="mini"> · {{ nombre(a) }}</span>
                  </span>
                </div>
              </template>
              <div v-else class="mini">Todos los artículos en producción tienen receta de materia prima.</div>
            </div>
          </div>

          <div class="nota">
            <strong>Cómo leer el tablero.</strong> La <em>existencia</em> es la suma de movimientos del corte
            vigente (entradas menos salidas); lo <em>reservado</em> es informativo y no se resta. El
            <em>ritmo</em> de cada máquina son las piezas que produce por día de la semana —piezas buenas más piezas
            reportadas con incidencia (lo que en piso se llama scrap: incidencias que no bloquean producción, del módulo de
            Incidencias); su clasificación en scrap o retrabajo no cambia el consumo— con la historia elegida; multiplicado por la materia prima por pieza de su receta (con merma) da el
            <em>requerido</em> por día. <span class="sem rojo">Rojo</span> se agota esta misma semana;
            <span class="sem ambar">Ámbar</span> alcanza esta semana pero se agota la siguiente;
            <span class="sem verde">Verde</span> cubre las dos semanas; <span class="sem sinDato">Sin dato</span>
            no tiene consumo pronosticado o no tiene inventario capturado. <em>Pedir antes de</em> es la fecha en
            que se agota menos el tiempo de entrega del proveedor, cuando está capturado.
          </div>
        </template>
      </main>
    </div>
  </v-col>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";

const ORDEN_SEMAFORO = { rojo: 0, ambar: 1, verde: 2, sinDato: 3 };
const ETIQUETAS_AVISO = {
  SinCorte: "Sin corte de inventario",
  SinInventario: "Sin inventario",
  RecetaPorLote: "Receta por lote",
  OperacionIntermedia: "Operación intermedia",
  PasoIntermedio: "Paso intermedio",
  SinScrap: "Sin incidencias capturadas",
  RecetaNoLegible: "Receta no legible",
  DobleConteo: "Doble conteo",
  ExistenciaNegativa: "Existencia negativa",
};
/** Avisos que se pintan en rojo: los demás van en ámbar (advertencia de lectura). */
const AVISOS_ROJOS = { ExistenciaNegativa: true, SinCorte: true, RecetaNoLegible: true };
const TEXTO_SEMAFORO = { rojo: "Rojo", ambar: "Ámbar", verde: "Verde", sinDato: "Sin dato" };

export default {
  name: "ReportePlaneacionMateriaPrima",
  props: {
    /** El dashboard marca active cuando esta pestaña es la visible. */
    active: { type: Boolean, default: true },
  },
  data() {
    return {
      /** Respuesta completa de /PlaneacionMateriaPrima/Tablero; null hasta la primera carga. */
      tablero: null,
      error: "",
      cargando: false,
      /** Catálogo de almacenes para el filtro ({ id, nombre }). */
      almacenes: [],
      /** Filtros enviados al back: almacén (null = todos) y semanas de historia. */
      idAlmacen: null,
      semanas: 4,
      /** Tarjeta seleccionada: rojo | ambar | verde | sinInventario | sinReceta | null. */
      filtro: null,
      /** IdArticulo de la materia prima con el detalle desplegado. */
      abierta: null,
      /** "consultado hace N s", se recalcula cada segundo. */
      reloj: "",
      diasSemana: ["Lun", "Mar", "Mie", "Jue", "Vie", "Sab", "Dom"],
    };
  },
  mounted() {
    this.ultimo = 0;
    this.tTic = setInterval(this.tic, 1000);
    // El back guarda el tablero un rato; recargar cada 5 min con la pestaña
    // visible basta para que el "hoy" y la existencia no se queden viejos.
    this.tCarga = setInterval(() => { if (this.active) this.cargar(true); }, 5 * 60 * 1000);
    this.cargarAlmacenes();
    this.cargar();
  },
  beforeDestroy() {
    clearInterval(this.tTic);
    clearInterval(this.tCarga);
  },
  computed: {
    ...mapState({ nombreEmpresa: "nombreEmpresa" }),
    T() { return this.tablero || {}; },
    subtitulo() {
      if (!this.tablero) return this.error ? "sin datos" : "cargando…";
      return (this.nombreEmpresa || "") + " · Hoy " + this.fecha(this.val(this.T, "hoy", "Hoy")) +
        " · semana del " + this.fecha(this.val(this.T, "inicioSemana", "InicioSemana")) +
        " · almacén: " + (this.val(this.T, "almacen", "Almacen") || "Todos");
    },
    avisos() { return this.arr(this.val(this.T, "avisos", "Avisos")); },
    resumen() { return this.val(this.T, "resumen", "Resumen") || {}; },
    tarjetas() {
      const R = this.resumen, v = (a, b) => this.num(this.val(R, a, b));
      return [
        { clave: "rojo", titulo: "Se agotan esta semana", n: v("rojas", "Rojas"),
          color: v("rojas", "Rojas") ? "al" : "", ayuda: "Materias primas que se agotan esta misma semana" },
        { clave: "ambar", titulo: "Se agotan la semana siguiente", n: v("ambar", "Ambar"),
          color: v("ambar", "Ambar") ? "am" : "", ayuda: "Alcanzan esta semana pero se agotan la siguiente" },
        { clave: "verde", titulo: "Cubren las dos semanas", n: v("verdes", "Verdes"),
          color: v("verdes", "Verdes") ? "ok" : "", ayuda: "Cubren hasta el domingo de la semana siguiente" },
        { clave: "sinReceta", titulo: "Artículos sin receta de MP", n: v("sinReceta", "SinReceta"),
          color: v("sinReceta", "SinReceta") ? "am" : "", ayuda: "Artículos en producción sin materia prima en su receta" },
        { clave: "sinInventario", titulo: "MP sin inventario", n: v("sinInventario", "SinInventario"),
          color: v("sinInventario", "SinInventario") ? "al" : "", ayuda: "Materias primas requeridas sin ningún movimiento de inventario" },
      ];
    },
    tituloFiltro() {
      const t = this.tarjetas.find(c => c.clave === this.filtro);
      return t ? t.titulo : "";
    },
    /** Materias primas ordenadas: rojo, ámbar, verde, sin dato; luego faltante mayor primero; luego código. */
    mps() {
      const sem = m => ORDEN_SEMAFORO[this.val(m, "semaforo", "Semaforo")];
      return this.arr(this.val(this.T, "materiasPrimas", "MateriasPrimas")).slice().sort((a, b) => {
        const sa = sem(a) === undefined ? 9 : sem(a), sb = sem(b) === undefined ? 9 : sem(b);
        if (sa !== sb) return sa - sb;
        const fa = this.num(this.val(a, "faltante", "Faltante")), fb = this.num(this.val(b, "faltante", "Faltante"));
        if (fa !== fb) return fb - fa;
        return String(this.val(a, "codigo", "Codigo") || "").localeCompare(String(this.val(b, "codigo", "Codigo") || ""));
      });
    },
    mpsFiltradas() {
      const f = this.filtro;
      if (!f || f === "sinReceta") return this.mps;
      if (f === "sinInventario") return this.mps.filter(m => this.esSinInventario(m));
      return this.mps.filter(m => this.val(m, "semaforo", "Semaforo") === f);
    },
    /** Producción: activas primero, luego por máquina. */
    produccion() {
      return this.arr(this.val(this.T, "produccion", "Produccion")).slice().sort((a, b) => {
        const aa = this.val(a, "activa", "Activa") ? 0 : 1, ab = this.val(b, "activa", "Activa") ? 0 : 1;
        if (aa !== ab) return aa - ab;
        return String(this.val(a, "maquina", "Maquina") || "").localeCompare(String(this.val(b, "maquina", "Maquina") || ""));
      });
    },
    sinReceta() { return this.arr(this.val(this.T, "articulosSinRecetaMP", "ArticulosSinRecetaMP")); },
  },
  watch: {
    active(v) { if (v) this.cargar(true); },
  },
  methods: {
    /* ───────── helpers de lectura y formato ───────── */

    // El back serializa en PascalCase, pero otros puntos de la plataforma llegan
    // en camelCase: se aceptan las dos capitalizaciones.
    val(obj, ...keys) {
      for (const key of keys) {
        if (obj && obj[key] !== undefined && obj[key] !== null) return obj[key];
      }
      return undefined;
    },
    arr(v) { return Array.isArray(v) ? v : []; },
    num(v, fallback = 0) {
      const n = Number(v);
      return Number.isFinite(n) ? n : fallback;
    },
    fmt(v, min, max) {
      if (v === undefined || v === null || v === "") return "—";
      return this.num(v).toLocaleString("es-MX", { minimumFractionDigits: min, maximumFractionDigits: max });
    },
    f0(v) { return this.fmt(v, 0, 0); },
    f1(v) { return this.fmt(v, 0, 1); },
    f2(v) { return this.fmt(v, 2, 2); },
    f4(v) { return this.fmt(v, 2, 4); },
    /** "2026-09-10" → "10 sep 2026". Las fechas llegan como texto: no se pasan por Date para no mover el día. */
    fecha(s) {
      if (!s || typeof s !== "string" || s.length < 10) return "—";
      const meses = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
      const mes = meses[Number(s.slice(5, 7)) - 1];
      return mes ? Number(s.slice(8, 10)) + " " + mes + " " + s.slice(0, 4) : s.slice(0, 10);
    },
    /** Las notas del back traen fechas "yyyy-MM-dd"; se muestran como el resto de las fechas. */
    nota(texto) {
      if (!texto) return "";
      return String(texto).replace(/\d{4}-\d{2}-\d{2}/g, f => this.fecha(f));
    },
    etiquetaAviso(tipo) { return ETIQUETAS_AVISO[tipo] || "Aviso"; },
    claseAviso(tipo) { return AVISOS_ROJOS[tipo] ? "rojo" : "ambar"; },
    textoSemaforo(s) { return TEXTO_SEMAFORO[s] || "Sin dato"; },
    claseSemaforo(s) { return ORDEN_SEMAFORO[s] === undefined ? "sinDato" : s; },
    // Etiquetas de cara al cliente: si el catálogo no trae código o nombre se dice
    // así, nunca se enseña el identificador interno.
    codigo(o) { return this.val(o, "codigo", "Codigo") || "Sin código"; },
    nombre(o) { return this.val(o, "nombre", "Nombre") || "Sin nombre"; },
    maquina(o) { return this.val(o, "maquina", "Maquina") || "Sin nombre"; },
    /** Corrida desplazada por otra orden en la misma máquina (nota del back). */
    esReemplazada(p) { return /^Reemplazada/i.test(String(this.val(p, "nota", "Nota") || "")); },
    /** Piezas por día de la semana de una corrida; d = "Lun".."Dom" (camelCase "lun".."dom"). */
    ritmo(p, d) {
      const r = this.val(p, "ritmoPorDia", "RitmoPorDia");
      return this.val(r, d.toLowerCase(), d);
    },
    /** Bandera del back: la materia prima no tiene ningún movimiento de inventario. */
    esSinInventario(m) { return this.val(m, "sinInventario", "SinInventario") === true; },

    /* ───────── interacción ───────── */

    filtrar(clave) {
      this.filtro = this.filtro === clave ? null : clave;
      if (this.filtro === "sinReceta" && this.$refs.sinReceta) {
        this.$refs.sinReceta.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    abrir(id) { this.abierta = this.abierta === id ? null : id; },

    /* ───────── datos ───────── */

    async cargarAlmacenes() {
      try {
        const r = await axios.get("/Almacenes");
        this.almacenes = this.arr(r.data).map(a => ({
          id: this.val(a, "idAlmacen", "IdAlmacen"),
          nombre: this.val(a, "nombre", "Nombre") || this.val(a, "codigo", "Codigo") || "",
        })).filter(a => a.id !== undefined);
      } catch (e) {
        // Sin catálogo se sigue con "Todos"; el tablero no depende de él.
        this.almacenes = [];
      }
    },
    parametros() {
      return { idAlmacen: this.idAlmacen, semanas: this.semanas };
    },
    async cargar(silencioso) {
      if (!silencioso) this.cargando = true;
      try {
        const r = await axios.get("/PlaneacionMateriaPrima/Tablero", { params: this.parametros() });
        this.recibir(r.data);
      } catch (e) {
        this.error = this.mensaje(e, "No se pudo consultar el reporte");
      } finally {
        this.cargando = false;
      }
    },
    /** Botón "Actualizar ahora": recalcula en el back sin usar lo guardado. */
    async refrescar() {
      this.cargando = true;
      try {
        const r = await axios.post("/PlaneacionMateriaPrima/Refrescar", null, { params: this.parametros() });
        this.recibir(r.data);
      } catch (e) {
        this.error = this.mensaje(e, "No se pudo actualizar el reporte");
      } finally {
        this.cargando = false;
      }
    },
    recibir(d) {
      this.tablero = d && typeof d === "object" ? d : null;
      this.error = this.tablero ? "" : "El servidor no devolvió el tablero.";
      this.ultimo = Date.now();
      this.tic();
    },
    /**
     * Mensaje de error de cara al usuario. El filtro global de excepciones del
     * back responde { TrackId, Code, Msg }; otros puntos mandan error/message.
     * Si no llega un texto del servidor se dice en español, no el texto de la red.
     */
    mensaje(e, prefijo) {
      const d = e && e.response && e.response.data;
      let texto = "";
      if (typeof d === "string") texto = d;
      else if (d) texto = d.Msg || d.msg || d.error || d.Error || d.message || d.Message || "";
      return texto ? prefijo + ": " + texto : prefijo + "; intenta de nuevo en unos minutos.";
    },
    tic() {
      if (!this.tablero) { this.reloj = ""; return; }
      const seg = this.num(this.val(this.T, "consultaHaceSegundos", "ConsultaHaceSegundos")) +
        Math.round((Date.now() - this.ultimo) / 1000);
      this.reloj = "consultado hace " + (seg < 90 ? seg + " s" : Math.round(seg / 60) + " min");
    },
  },
};
</script>

<style>
/* Todo bajo #planeacion-mp para no tocar al resto de la plataforma; no es
   "scoped" para compartir el mismo molde visual que r113/r116. */
#planeacion-mp{--teal:#197F72;--teald:#0D564F;--rojo:#C00000;--ambar:#BF8F00;--azul:#2E74B5;
 --fondo:#f4f6f6;--tarjeta:#fff;--texto:#1c2321;--suave:#6b7a77;--linea:#dfe5e4;}
@media(prefers-color-scheme:dark){#planeacion-mp{--fondo:#111615;--tarjeta:#1a2120;
 --texto:#e8edec;--suave:#8fa19d;--linea:#2b3634;}}
#planeacion-mp{margin:0;background:var(--fondo);color:var(--texto);
 font:14px/1.45 -apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif;
 border-radius:8px;overflow:hidden}
#planeacion-mp *{box-sizing:border-box}
#planeacion-mp .pm-head{background:var(--teal);color:#fff;padding:14px 20px;display:flex;
 align-items:center;gap:16px;flex-wrap:wrap}
#planeacion-mp .pm-head h1{margin:0;font-size:17px;font-weight:600;letter-spacing:.2px}
#planeacion-mp .pm-head .sub{font-size:12px;opacity:.85}
#planeacion-mp .pm-head .der{margin-left:auto;display:flex;align-items:center;gap:12px;font-size:12px;
 flex-wrap:wrap}
#planeacion-mp .pm-head label{display:flex;align-items:center;gap:6px}
#planeacion-mp .pm-head select{font:inherit;padding:4px 8px;border:0;border-radius:6px;
 background:rgba(255,255,255,.92);color:var(--texto)}
#planeacion-mp .reloj{opacity:.85;white-space:nowrap}
#planeacion-mp button{font:inherit;border:0;border-radius:6px;padding:6px 12px;cursor:pointer;
 background:var(--teald);color:#fff}
#planeacion-mp button.claro{background:rgba(255,255,255,.18)}
#planeacion-mp button:hover{filter:brightness(1.12)}
#planeacion-mp button:disabled{opacity:.6;cursor:default}
#planeacion-mp main{padding:18px 20px 40px;max-width:1600px;margin:0 auto;display:block}
#planeacion-mp .err{background:rgba(192,0,0,.12);color:var(--rojo);padding:9px 12px;
 border-radius:8px;margin-bottom:14px;font-size:13px}
#planeacion-mp .vacio{color:var(--suave);padding:22px;text-align:center;font-size:13px}
#planeacion-mp .mini{font-size:11px;color:var(--suave)}
#planeacion-mp a{color:var(--teal)}
#planeacion-mp .avisos{display:flex;flex-direction:column;gap:6px;margin-bottom:10px}
#planeacion-mp .aviso{background:rgba(191,143,0,.10);border:1px solid rgba(191,143,0,.45);
 border-radius:8px;padding:8px 12px;font-size:13px;display:flex;gap:10px;align-items:center;flex-wrap:wrap}
#planeacion-mp .aviso .tag{font-size:10.5px;font-weight:700;text-transform:uppercase;letter-spacing:.4px;
 color:var(--ambar);white-space:nowrap}
#planeacion-mp .aviso.rojo{background:rgba(192,0,0,.08);border-color:rgba(192,0,0,.45)}
#planeacion-mp .aviso.rojo .tag{color:var(--rojo)}
#planeacion-mp .sin-te{color:var(--ambar);font-weight:600;white-space:normal;display:inline-block;
 max-width:120px;line-height:1.25;cursor:help}
#planeacion-mp .nota-td.reemplazada{color:var(--azul);font-weight:600}
#planeacion-mp .corte{margin-bottom:14px}
#planeacion-mp .cards{display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));
 gap:12px;margin-bottom:18px}
#planeacion-mp .card{background:var(--tarjeta);border:1px solid var(--linea);border-radius:10px;
 padding:12px 14px}
#planeacion-mp .card .n{font-size:26px;font-weight:600;line-height:1.1}
#planeacion-mp .card .t{font-size:11px;color:var(--suave);text-transform:uppercase;
 letter-spacing:.6px;margin-top:2px}
#planeacion-mp .card.al .n{color:var(--rojo)}
#planeacion-mp .card.am .n{color:var(--ambar)}
#planeacion-mp .card.ok .n{color:var(--teal)}
#planeacion-mp .card.clic{cursor:pointer;transition:border-color .12s}
#planeacion-mp .card.clic:hover{border-color:var(--teal)}
#planeacion-mp .card.sel{border-color:var(--teal);box-shadow:0 0 0 2px rgba(25,127,114,.25)}
#planeacion-mp .bloque{margin-bottom:20px}
#planeacion-mp .bloque.marcado .caja{border-color:var(--teal);box-shadow:0 0 0 2px rgba(25,127,114,.25)}
#planeacion-mp .bt{font-size:13px;font-weight:600;color:var(--teal);text-transform:uppercase;
 letter-spacing:.5px;margin-bottom:8px}
#planeacion-mp .bt .mini{text-transform:none;letter-spacing:0;font-weight:400}
#planeacion-mp .tabla{background:var(--tarjeta);border:1px solid var(--linea);border-radius:10px;
 overflow-x:auto}
#planeacion-mp table{border-collapse:collapse;width:100%;font-size:12.5px}
#planeacion-mp th,#planeacion-mp td{padding:6px 7px;text-align:center;
 border-bottom:1px solid var(--linea);white-space:nowrap;vertical-align:middle}
#planeacion-mp th{background:var(--teal);color:#fff;font-weight:600;font-size:10.5px;
 text-transform:uppercase;letter-spacing:.3px;line-height:1.25}
#planeacion-mp th.izq,#planeacion-mp td.izq{text-align:left}
#planeacion-mp td.num{text-align:right;font-variant-numeric:tabular-nums}
#planeacion-mp td.num.mal{color:var(--rojo);font-weight:700}
#planeacion-mp td.num.cero{color:var(--suave)}
#planeacion-mp tbody tr.clic{cursor:pointer}
#planeacion-mp tbody tr:hover{background:rgba(25,127,114,.06)}
#planeacion-mp tbody tr.rojo{background:rgba(192,0,0,.10)}
#planeacion-mp tbody tr.rojo:hover{background:rgba(192,0,0,.16)}
#planeacion-mp tbody tr.ambar{background:rgba(255,206,0,.22)}
#planeacion-mp tbody tr.ambar:hover{background:rgba(255,206,0,.30)}
#planeacion-mp tbody tr.apagada td{color:var(--suave)}
#planeacion-mp tbody tr.det td{background:var(--fondo);padding:10px 12px;white-space:normal}
#planeacion-mp tbody tr.det:hover{background:transparent}
#planeacion-mp .cod{font-weight:600;font-size:13.5px}
#planeacion-mp .fl{font-size:10px;opacity:.55;margin-left:4px}
#planeacion-mp .nom{max-width:280px;overflow:hidden;text-overflow:ellipsis}
#planeacion-mp .nota-mp{color:var(--texto);opacity:.8}
#planeacion-mp .nota-td{max-width:300px;white-space:normal;line-height:1.3}
#planeacion-mp .sem{display:inline-block;padding:1px 9px;border-radius:20px;font-size:11px;
 font-weight:700;border:1px solid var(--linea)}
#planeacion-mp .sem.rojo{background:rgba(192,0,0,.15);color:var(--rojo)}
#planeacion-mp .sem.ambar{background:rgba(191,143,0,.20);color:var(--ambar)}
#planeacion-mp .sem.verde{background:rgba(25,127,114,.16);color:var(--teal)}
#planeacion-mp .sem.sinDato{background:rgba(0,0,0,.07);color:var(--suave)}
#planeacion-mp .pill{display:inline-block;padding:2px 9px;border-radius:20px;font-size:11px;font-weight:600}
#planeacion-mp .pill.on{background:rgba(25,127,114,.16);color:var(--teal)}
#planeacion-mp .pill.off{background:rgba(0,0,0,.07);color:var(--suave)}
#planeacion-mp .pill.sin{background:rgba(191,143,0,.18);color:var(--ambar)}
@media(prefers-color-scheme:dark){#planeacion-mp .pill.off,#planeacion-mp .sem.sinDato{background:rgba(255,255,255,.08)}}
#planeacion-mp .paneles{display:grid;grid-template-columns:repeat(auto-fit,minmax(420px,1fr));gap:12px}
#planeacion-mp .panel{background:var(--tarjeta);border:1px solid var(--teal);border-radius:10px;
 padding:10px 14px 12px;overflow-x:auto}
#planeacion-mp .ph{font-size:12px;font-weight:600;color:var(--teal);text-transform:uppercase;
 letter-spacing:.5px;margin-bottom:7px}
#planeacion-mp .ph .mini{text-transform:none;letter-spacing:0;font-weight:400}
#planeacion-mp table.sub{font-size:12px}
#planeacion-mp table.sub th{font-size:10px}
#planeacion-mp table.sub tr.neg td{background:rgba(192,0,0,.06)}
#planeacion-mp .caja{background:var(--tarjeta);border:1px solid var(--linea);border-radius:10px;
 padding:12px 14px;font-size:13px}
#planeacion-mp .caja p{margin:0 0 8px}
#planeacion-mp .chips{display:flex;flex-wrap:wrap;gap:6px}
#planeacion-mp .chip{display:inline-block;padding:2px 9px;border-radius:6px;font-size:12px;
 background:rgba(191,143,0,.14);border:1px solid rgba(191,143,0,.35);white-space:nowrap}
#planeacion-mp .nota{font-size:12px;color:var(--suave);margin-top:14px;line-height:1.6}
</style>
